/**
 * Module for adding more homes to the self-guided tour feature.
 */

import { store } from '@redux/store';
import { fromEvent } from 'rxjs';
import {
  selectAdditionalHomes,
  selectModalStep,
  selectSelectedSlot
} from '../redux/selectors';
import ModalStep from '../enums/modalStep';
import { setAdditionalHomes, setModalStep } from '../redux/slices';
import BrokenImageHandler from '../utils/brokenImageHandler';
import cardSkeleton from './cardSkeleton';
import cardSpecHome from './cardSpecHome';

/**
 * Class representing the functionality for adding more homes for self-guided tours.
 */
export default class TourConfiguration {
  // Properties

  listContainerSelector = '[data-self-guided-tour-sibling-list]';

  checkboxChangeSubscription = null;

  /**
   * Creates an instance of AddMoreHomes.
   * @param {Object} rootComponent - The root component providing context and event subscriptions.
   */
  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.nextStep = ModalStep.TermsAndConditions;
    this.previousStep = ModalStep.DateTimeSelection;
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
    this.brokenImages = new BrokenImageHandler();
  }

  /**
   * Retrieves the maximum additional homes limit based on the selected slot duration.
   * @returns {number} The additional homes limit.
   */
  static getAdditionalHomesLimit() {
    const state = store.getState();
    const slot = selectSelectedSlot(state);
    return slot.Duration === 30 ? 2 : 5;
  }

  /**
   * Retrieves the maximum number of homes based on the duration.
   * @param {number} duration - The duration in minutes.
   * @returns {string} The maximum number of homes.
   */
  static getMaxFromDuration(duration) {
    if (duration === 30) {
      return 'three';
    }
    return 'six';
  }

  /**
   * Handles DOM updates, including initiating fetching of tourable siblings.
   * @param {Event} event - The DOM update event.
   */
  onDomUpdate(event) {
    // Check if the event is for the Tour Configuration step and set up the checkbox change subscription
    if (event.detail && event.detail.step === ModalStep.TourConfiguration) {
      this.updateCardList();
      this.rootComponent.setPrimaryButtonDisabled(false);
      this.rootComponent.setSecondaryButtonDisabled(false);

      const listContainer = this.getListContainer();
      if (!listContainer) {
        return;
      }
      this.checkboxChangeSubscription = fromEvent(
        listContainer,
        'change'
      ).subscribe(this.handleCheckbox.bind(this));
    } else if (this.checkboxChangeSubscription) {
      this.checkboxChangeSubscription.unsubscribe();
      this.checkboxChangeSubscription = null;
      this.brokenImages.unbind();
    }
  }

  /**
   * Returns a handler for checkbox change events.
   * @param {Event} event - The checkbox change event.
   * @returns {Function} The checkbox event handler.
   */
  handleCheckbox(event) {
    const limit = TourConfiguration.getAdditionalHomesLimit();
    const checkboxes = this.getListContainer().querySelectorAll(
      'input[type="checkbox"]'
    );
    const checkedCheckboxes = Array.from(checkboxes).filter(
      checkbox => checkbox.checked
    );
    const checkedCount = checkedCheckboxes.length;

    if (checkedCount >= limit && event.target.checked) {
      this.rootComponent.showNotification(
        'You’ve reached your home tour limit.'
      );
    } else {
      this.rootComponent.removeNotification();
    }

    // Disable unchecked checkboxes if the limit is reached
    checkboxes.forEach(checkbox => {
      if (!checkbox.checked) {
        checkbox.disabled = checkedCount >= limit;
      }
    });

    const selectedHomes = checkedCheckboxes.map(
      checkbox => checkbox.dataset.specId
    );
    const slicedSelectedHomes = selectedHomes.slice(
      0,
      TourConfiguration.getAdditionalHomesLimit()
    );
    store.dispatch(
      setAdditionalHomes({ additionalHomes: slicedSelectedHomes })
    );
  }

  /**
   * Retrieves the DOM element for the list container.
   * @returns {HTMLElement} The list container element.
   */
  getListContainer() {
    return document.querySelector(this.listContainerSelector);
  }

  /**
   * Updates the card list in the UI with the fetched tourable sibling homes.
   */
  updateCardList() {
    // clear previous subscriptions before we rerender the card list
    this.brokenImages.unbind();
    const state = store.getState();
    const additionalHomes = selectAdditionalHomes(state);
    const listContainer = this.getListContainer();
    const limit = TourConfiguration.getAdditionalHomesLimit();
    const checkedCount = additionalHomes.length;
    const hasReachedLimit = checkedCount >= limit;
    const { noPhotoUrl } = this.rootComponent.getSpecDetails();

    listContainer.innerHTML = Object.values(this.rootComponent.otherHomes)
      .map(
        sibling =>
          `<li class="nhs-u-display--flex nhs-u-width--full">${cardSpecHome({
            id: sibling.Id,
            address: sibling.Address,
            planName: sibling.PlanName,
            squareFeet: sibling.SquareFeet,
            bathrooms: sibling.Bathrooms,
            bedrooms: sibling.Bedrooms,
            garages: sibling.Garages,
            price: sibling.Price,
            imageUrl:
              sibling.ImageUrl == null || sibling.ImageUrl === ''
                ? noPhotoUrl
                : sibling.ImageUrl,
            isChecked: additionalHomes.includes(sibling.Id),
            isDisabled: hasReachedLimit && !additionalHomes.includes(sibling.Id)
          })}</li>`
      )
      .join('');

    if (hasReachedLimit) {
      this.rootComponent.showNotification(
        'You’ve reached your home tour limit.'
      );
    }
    this.brokenImages.bind('[data-on-error-src]', listContainer);
  }

  /**
   * Handles the primary action for the tour configuration.
   * Dispatches an action to update the modal step in the store with the next step.
   *
   */
  handlePrimaryAction() {
    store.dispatch(setModalStep({ modalStep: this.nextStep }));
  }

  /**
   * Adds the spec IDs of the checked cards to the redux store.
   */
  handleSecondaryAction() {
    store.dispatch(setModalStep({ modalStep: this.previousStep }));
    store.dispatch(setAdditionalHomes({ additionalHomes: [] }));
  }

  static renderSkeletonList(number = 3) {
    let count = 0;
    let skeletonList = '';

    while (count < number) {
      skeletonList += `
        <li class="nhs-u-display--flex nhs-u-width--full">
          ${cardSkeleton()}
        </li>
      `;
      count += 1;
    }
    return skeletonList;
  }

  /**
   * Determines if the current modal step in the state corresponds to the Tour Configuration step.
   *
   * @param {Object} state - The current application state.
   * @returns {boolean} - Returns `true` if the current modal step is `ModalStep.TourConfiguration`, otherwise `false`.
   */
  static isTourStep(state) {
    return selectModalStep(state) === ModalStep.TourConfiguration;
  }

  /**
   * Renders the HTML for the add more homes component.
   * @returns {string}
   */
  render() {
    const state = store.getState();
    const slot = selectSelectedSlot(state);
    const { communityName, brandName } = this.rootComponent.getSpecDetails();

    return `<div class="nhs-c-self-guided-left-panel">
              <div>
                <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Add more homes to your self-tour</h1>

                <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

                <p class="nhs-c-label--black-900-body-1-medium-regular">There are additional homes available to tour in ${communityName} by ${brandName}.
                 Your self-tour allows up to ${TourConfiguration.getMaxFromDuration(
                   slot.Duration
                 )} homes for a ${slot.Duration} minute time reservation.</p>
              </div>
              <ol class="nhs-o-flex-column-items-start nhs-u-gap--600" data-self-guided-tour-sibling-list>
                ${TourConfiguration.renderSkeletonList()}
              </ol>
            </div>`;
  }
}
